// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {resolveComposerHolidayPeekPack} from '@app/features/holiday/components/ComposerHolidayPeekResolvers';
import {resolveHolidaySidePeekPack} from '@app/features/holiday/components/HolidaySidePeekResolvers';
import {
	BAT_COUNT_END_MAX,
	canStartHolidayFlyby,
	debugFlockCount,
	displayWaveFlockCount,
	extraBatFlockChance,
	extraFlockStaggerMs,
	FLYBY_DAYLIGHT_COUNT_KEEP,
	FLYBY_EXTRA_FLOCK_MAX,
	FLYBY_REACTION_GAP_MS,
	flybyCooldownMs,
	halloweenFlybyIntensity,
	isHalloweenAmbientBatsEnabled,
	isHalloweenFlybyNight,
	isHalloweenFlyingBatsEnabled,
	isHalloweenFlyingBatsForced,
	planBatFlock,
	postedMessageFlybyReason,
	shouldStartChannelSwitchFlyby,
	shouldStartSendFlyby,
} from '@app/features/holiday/packs/halloween/flyingBats';
import {
	HALLOWEEN_SCARE_MIN_INTENSITY,
	type HolidayScareReason,
	halloweenScareChance,
	halloweenScareCooldownMs,
	halloweenScareIntensity,
	isHalloweenScareForced,
	isHalloweenScaresEnabled,
} from '@app/features/holiday/packs/halloween/scares';
import {HALLOWEEN_SIDE_PEEK_PACK, nextHalloweenSidePeekerId} from '@app/features/holiday/packs/halloween/sidePeekers';
import ComposerHolidayPeekSession from '@app/features/holiday/state/ComposerHolidayPeek';
import Holiday, {type HolidayScareMode, type HolidayScareVariant} from '@app/features/holiday/state/Holiday';
import {isHolidayCategoryEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {toOverlayScopeOrigin, withReactionChipOrigin} from '@app/features/holiday/utils/findReactionOrigin';
import {cooldownElapsed} from '@app/features/holiday/utils/holidayCooldown';
import {getHolidayFlybyCrossWidthPx} from '@app/features/holiday/utils/holidayFlybyHost';
import {holidayOverlayAnimationsRun} from '@app/features/holiday/utils/holidayOverlayAnimations';
import {getHolidayScareHost} from '@app/features/holiday/utils/holidayScareHost';
import {isHolidaySeasonEnabled} from '@app/features/holiday/utils/holidaySeason';
import {isUnicodeBatReaction} from '@app/features/holiday/utils/isUnicodeBatReaction';
import {isUnicodeHeartReaction} from '@app/features/holiday/utils/isUnicodeHeartReaction';
import type {ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';

const DEBUG_PEEK_ROLLS = false;

function spawnCuteGhost(origin: {x: number; y: number}): void {
	Holiday.spawnCuteGhost(origin.x, origin.y);
}

const EXTRA_FLOCK_REASONS = new Set(['spoopy', 'send', 'channel-switch', 'ambient', 'debug-bats', 'debug-bats-full']);

function spawnExtraFlocks(
	now: number,
	intensity: number,
	viewportWidthPx: number,
	extraRoll: number,
	options?: {force?: boolean; count?: number; countKeep?: number},
): void {
	let roll = extraRoll;
	let delayMs = 0;
	for (let index = 0; index < FLYBY_EXTRA_FLOCK_MAX; index++) {
		if (!options?.force && roll >= extraBatFlockChance(intensity)) return;
		delayMs += extraFlockStaggerMs(roll);
		const plan = planBatFlock(roll, viewportWidthPx, intensity, {
			count: options?.count,
			countKeep: options?.countKeep,
		});
		Holiday.spawnBatFlock({...plan, startDelayMs: delayMs}, now);
		roll = (roll * 1.6180339887) % 1;
	}
}

function spawnDisplayWave(now: number, viewportWidthPx: number, roll: number, extraRoll: number): number | null {
	const flockCount = displayWaveFlockCount(extraRoll);
	let seed = roll;
	let delayMs = 0;
	let firstId: number | null = null;
	for (let index = 0; index < flockCount; index++) {
		const plan = planBatFlock(seed, viewportWidthPx, 1, {count: debugFlockCount(seed)});
		if (plan.bats.length === 0) {
			seed = (seed * 1.6180339887) % 1;
			continue;
		}
		if (index > 0) delayMs += extraFlockStaggerMs(seed);
		const id = Holiday.spawnBatFlock({...plan, startDelayMs: index === 0 ? undefined : delayMs}, now);
		if (firstId == null) firstId = id;
		seed = (seed * 1.6180339887) % 1;
	}
	return firstId;
}

export function toggleBatsDisplayLoop(): boolean {
	return Holiday.toggleBatsDisplayLoop();
}

export function tryStartHolidayFlyby(
	reason: string,
	now = Date.now(),
	roll = Math.random(),
	viewportWidthPx = getHolidayFlybyCrossWidthPx(),
	origin?: {x: number; y: number},
	viewportHeightPx = typeof window === 'undefined' ? 800 : window.innerHeight,
	extraRoll = Math.random(),
): number | null {
	const staffSeason = reason === 'debug-bats';
	const demo = reason === 'debug-bats-demo';
	const full = reason === 'debug-bats-full';
	const display = reason === 'debug-bats-display';
	const reaction = reason === 'reaction-bat';
	// Spoopy is a word someone sent on purpose. It answers at any hour, at the full flock
	// size, and it does not wait out the shared bat clock. Send and channel-switch flybys stay
	// night-only and on that clock; ambient answers by day too, thinned to a stray or two.
	const spoopy = reason === 'spoopy';
	const ambient = reason === 'ambient';
	const artLoop = isHalloweenFlyingBatsForced();
	const forced = artLoop || demo || full || display;
	if (!forced) {
		if (ambient) {
			if (!isHalloweenAmbientBatsEnabled()) return null;
		} else if (!isHalloweenFlyingBatsEnabled()) {
			return null;
		}
	}
	const clock = new Date(now);
	const night = isHalloweenFlybyNight(clock);
	const intensity = forced ? 1 : halloweenFlybyIntensity(clock);
	if (
		!canStartHolidayFlyby({
			season: isHolidaySeasonEnabled('halloween'),
			flybysEnabled: ambient
				? isHolidayCategoryEnabled('backgroundEffects', 'halloween')
				: isHolidayCategoryEnabled('flybys', 'halloween'),
			reducedMotion: Accessibility.useReducedMotion,
			documentVisible: typeof document === 'undefined' ? true : !document.hidden,
			lastSpawnAt: Holiday.lastBatFlockAt,
			now,
			cooldownMs: flybyCooldownMs(intensity),
			night,
			forced,
			bypassCooldown: forced || reaction || staffSeason || spoopy,
			bypassNight: reaction || spoopy || ambient,
		})
	) {
		return null;
	}
	if (reaction && Holiday.lastReactionBatAt != null && now < Holiday.lastReactionBatAt + FLYBY_REACTION_GAP_MS) {
		return null;
	}
	if (display) return spawnDisplayWave(now, viewportWidthPx, roll, extraRoll);
	// Daylight thins reaction and ambient flocks to a stray or two. Early in the season that rounds
	// a flock of one down to nothing, which is the point: day bats only appear once flocks are big.
	const countKeep = !night && (reaction || ambient) ? FLYBY_DAYLIGHT_COUNT_KEEP : undefined;
	const plan =
		origin != null
			? planBatFlock(roll, viewportWidthPx, intensity, {
					path: 'rise',
					origin: toOverlayScopeOrigin(origin),
					viewportHeightPx,
					countKeep,
				})
			: planBatFlock(roll, viewportWidthPx, intensity, {
					count: demo ? debugFlockCount(roll) : full ? BAT_COUNT_END_MAX : undefined,
					countKeep,
				});
	if (plan.bats.length === 0) return null;
	const id = Holiday.spawnBatFlock(plan, now);
	if (reaction) Holiday.lastReactionBatAt = now;
	if (EXTRA_FLOCK_REASONS.has(reason)) {
		spawnExtraFlocks(now, intensity, viewportWidthPx, extraRoll, {
			force: full,
			count: full ? BAT_COUNT_END_MAX : undefined,
			countKeep,
		});
	}
	return id;
}

export function maybeStartFlybyFromPostedMessage(
	content: string,
	inCurrentChannel: boolean,
	now = Date.now(),
	roll = Math.random(),
	extraRoll = Math.random(),
): number | null {
	const reason = postedMessageFlybyReason(content, inCurrentChannel);
	if (reason == null) return null;
	const intensity = halloweenFlybyIntensity(new Date(now));
	if (reason === 'send' && !shouldStartSendFlyby(roll, intensity)) return null;
	return tryStartHolidayFlyby(
		reason,
		now,
		roll,
		getHolidayFlybyCrossWidthPx(),
		undefined,
		typeof window === 'undefined' ? 800 : window.innerHeight,
		extraRoll,
	);
}

export function maybeStartFlybyFromChannelSwitch(
	now = Date.now(),
	roll = Math.random(),
	extraRoll = Math.random(),
): number | null {
	if (!shouldStartChannelSwitchFlyby(new Date(now), roll)) return null;
	return tryStartHolidayFlyby(
		'channel-switch',
		now,
		roll,
		getHolidayFlybyCrossWidthPx(),
		undefined,
		typeof window === 'undefined' ? 800 : window.innerHeight,
		extraRoll,
	);
}

function spawnBatReactionFlock(origin: {x: number; y: number}): void {
	tryStartHolidayFlyby('reaction-bat', Date.now(), Math.random(), getHolidayFlybyCrossWidthPx(), origin);
}

export function maybeSpawnHeartReactionGhost(channelId: string, messageId: string, emoji: ReactionEmoji): void {
	if (!isHolidaySeasonEnabled('halloween')) return;
	if (!isHolidayCategoryEnabled('emojiReactions', 'halloween')) return;
	if (Accessibility.useReducedMotion) return;
	if (!isUnicodeHeartReaction(emoji)) return;

	withReactionChipOrigin(channelId, messageId, emoji, spawnCuteGhost);
}

export function maybeSpawnBatReactionFlock(channelId: string, messageId: string, emoji: ReactionEmoji): void {
	if (!isHalloweenFlyingBatsEnabled()) return;
	if (Accessibility.useReducedMotion) return;
	if (!isUnicodeBatReaction(emoji)) return;

	withReactionChipOrigin(channelId, messageId, emoji, spawnBatReactionFlock);
}

export function noteComposerDraftChange(
	channelId: string,
	value: string,
	now = Date.now(),
	roll = Math.random(),
): void {
	const pack = resolveComposerHolidayPeekPack();
	if (pack == null || Accessibility.useReducedMotion) {
		ComposerHolidayPeekSession.reset(channelId, now);
		return;
	}
	if (pack.forceArtLoop?.()) return;
	if (DEBUG_PEEK_ROLLS && import.meta.env.DEV) {
		console.debug('[composer-holiday-peek]', {channelId, valueLength: value.length, roll});
	}
	ComposerHolidayPeekSession.noteDraftChange(channelId, value, now, roll);
}

export function resetComposerHolidayPeek(channelId: string, now = Date.now()): void {
	ComposerHolidayPeekSession.reset(channelId, now);
}

export function maybeSpawnSidePeek(channelId: string, _messageId: string, peekerRoll = Math.random()): number | null {
	if (Accessibility.useReducedMotion) return null;
	if (channelId !== SelectedChannel.currentChannelId) return null;
	if (Holiday.hasKind('side-peek')) return null;
	const pack = resolveHolidaySidePeekPack();
	if (pack == null || pack.peekers.length === 0) return null;
	const peeker = pack.peekers[Math.floor(peekerRoll * pack.peekers.length)] ?? pack.peekers[0];
	if (peeker == null) return null;
	return Holiday.spawnSidePeek(pack.id, peeker.id, channelId);
}

/**
 * Scares are late-season only, capped at one at a time, and share a cooldown across every trigger
 * so three different triggers cannot stack. Each caller supplies its own chance for `reason`.
 */
export function maybeSpawnScare(
	reason: HolidayScareReason,
	now = Date.now(),
	roll = Math.random(),
	variant: HolidayScareVariant = 'skeleton',
): number | null {
	if (Accessibility.useReducedMotion) return null;
	if (typeof document !== 'undefined' && document.hidden) return null;
	// A scare with nowhere to render would still burn the cooldown, locking out the next one for up
	// to half an hour after showing nothing. Off-channel routes have no chat layout mounted.
	if (getHolidayScareHost() == null) return null;
	// Matches the CSS rule that pauses overlay animation while the window is not focused: spawning
	// then would freeze the sprite at its invisible first frame while still fading message content.
	if (!holidayOverlayAnimationsRun()) return null;
	if (!isHalloweenScareForced()) {
		if (!isHalloweenScaresEnabled()) return null;
		const intensity = halloweenScareIntensity(new Date(now));
		if (intensity < HALLOWEEN_SCARE_MIN_INTENSITY) return null;
		if (!cooldownElapsed(Holiday.lastScareAt, now, halloweenScareCooldownMs(intensity))) return null;
		if (roll >= halloweenScareChance(reason, intensity)) return null;
	}
	if (Holiday.hasKind('scare')) return null;
	return Holiday.spawnScare(variant, now);
}

/** `/scare` and `/scare-pop`. Bypasses season, chance, and cooldown; reduced motion still wins. */
export function debugSpawnScare(
	mode: HolidayScareMode = 'grow',
	variant: HolidayScareVariant = 'skeleton',
): number | null {
	if (Accessibility.useReducedMotion) return null;
	Holiday.removeKind('scare');
	// An art pass must not consume the real cooldown and lock out scares for half an hour.
	const lastScareAt = Holiday.lastScareAt;
	const id = Holiday.spawnScare(variant, Date.now(), mode);
	Holiday.lastScareAt = lastScareAt;
	return id;
}

export function debugSpawnHolidaySidePeek(): number | null {
	if (Accessibility.useReducedMotion) return null;
	const channelId = SelectedChannel.currentChannelId;
	if (channelId == null) return null;
	Holiday.removeKind('side-peek');
	const peekerId = nextHalloweenSidePeekerId();
	return Holiday.spawnSidePeek(HALLOWEEN_SIDE_PEEK_PACK.id, peekerId, channelId);
}
